import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  vx: number;
  vy: number;
  radius: number;
  baseRadius: number;
  opacity: number;
  baseOpacity: number;
  pulsePhase: number;
  isAccent: boolean;
}

interface MouseState {
  x: number;
  y: number;
  active: boolean;
}

export default function DynamicTechBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<MouseState>({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let particles: Particle[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    const initParticles = () => {
      const w = canvas.width;
      const h = canvas.height;
      const count = Math.min(Math.floor((w * h) / 15000), 120);
      particles = [];

      for (let i = 0; i < count; i++) {
        const isAccent = i % 12 === 0;
        const r = isAccent ? 2 + Math.random() * 1.5 : 1 + Math.random() * 1;
        const op = 0.3 + Math.random() * 0.5;
        const x = Math.random() * w;
        const y = Math.random() * h;

        particles.push({
          x,
          y,
          baseX: x,
          baseY: y,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          radius: r,
          baseRadius: r,
          opacity: op,
          baseOpacity: op,
          pulsePhase: Math.random() * Math.PI * 2,
          isAccent,
        });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    const draw = (time: number) => {
      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      ctx.clearRect(0, 0, w, h);

      // Interactions
      const repelRadius = 200;
      const repelStrength = 0.8;
      const returnSpeed = 0.05;

      particles.forEach((p) => {
        // Base drift
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around
        if (p.x < -50) { p.x = w + 50; p.baseX = p.x; }
        if (p.x > w + 50) { p.x = -50; p.baseX = p.x; }
        if (p.y < -50) { p.y = h + 50; p.baseY = p.y; }
        if (p.y > h + 50) { p.y = -50; p.baseY = p.y; }

        // Mouse interaction
        if (mouse.active) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < repelRadius && dist > 0) {
            // Repel force: push away from mouse
            const force = (1 - dist / repelRadius) * repelStrength;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;

            // Highlight: scale radius based on proximity
            const proximityFactor = 1 - dist / repelRadius;
            p.radius = p.baseRadius + proximityFactor * 3;
            p.opacity = Math.min(1, p.baseOpacity + proximityFactor * 0.5);
          } else {
            // Return to base position gently
            p.x += (p.baseX - p.x) * returnSpeed;
            p.y += (p.baseY - p.y) * returnSpeed;
            p.radius += (p.baseRadius - p.radius) * returnSpeed;
            p.opacity += (p.baseOpacity - p.opacity) * returnSpeed;
          }
        } else {
          // Mouse not active: return to base
          p.x += (p.baseX - p.x) * returnSpeed;
          p.y += (p.baseY - p.y) * returnSpeed;
          p.radius += (p.baseRadius - p.radius) * returnSpeed;
          p.opacity += (p.baseOpacity - p.opacity) * returnSpeed;
        }
      });

      // Draw connections (particle-to-particle)
      const maxDist = 150;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.12;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(192, 192, 192, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw connections from mouse to nearby particles
      if (mouse.active) {
        const mouseMaxDist = 180;
        particles.forEach((p) => {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < mouseMaxDist) {
            const alpha = (1 - dist / mouseMaxDist) * 0.25;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(p.x, p.y);

            // Accent particles get red lines, others silver
            if (p.isAccent) {
              ctx.strokeStyle = `rgba(220, 38, 38, ${alpha})`;
              ctx.lineWidth = 1;
            } else {
              ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
              ctx.lineWidth = 0.5;
            }
            ctx.stroke();
          }
        });

        // Draw mouse cursor glow
        const cursorGlow = ctx.createRadialGradient(
          mouse.x, mouse.y, 0,
          mouse.x, mouse.y, 30
        );
        cursorGlow.addColorStop(0, "rgba(220, 38, 38, 0.15)");
        cursorGlow.addColorStop(0.6, "rgba(220, 38, 38, 0.05)");
        cursorGlow.addColorStop(1, "rgba(220, 38, 38, 0)");
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 30, 0, Math.PI * 2);
        ctx.fillStyle = cursorGlow;
        ctx.fill();

        // Cursor center dot
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(220, 38, 38, 0.6)";
        ctx.fill();
      }

      // Draw particles
      particles.forEach((p) => {
        const pulse = Math.sin(time * 0.001 + p.pulsePhase) * 0.3 + 0.7;
        const currentOpacity = p.opacity * pulse;
        const currentRadius = p.radius * pulse;

        // Glow
        if (p.isAccent || p.radius > p.baseRadius * 1.5) {
          const glowRadius = currentRadius * 6;
          const glow = ctx.createRadialGradient(
            p.x, p.y, 0,
            p.x, p.y, glowRadius
          );
          if (p.isAccent) {
            glow.addColorStop(0, `rgba(220, 38, 38, ${currentOpacity * 0.2})`);
            glow.addColorStop(1, "rgba(220, 38, 38, 0)");
          } else {
            glow.addColorStop(0, `rgba(255, 255, 255, ${currentOpacity * 0.15})`);
            glow.addColorStop(1, "rgba(255, 255, 255, 0)");
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
          ctx.fillStyle = glow;
          ctx.fill();
        }

        // Core dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, currentRadius, 0, Math.PI * 2);
        if (p.isAccent) {
          ctx.fillStyle = `rgba(220, 38, 38, ${currentOpacity})`;
        } else if (p.radius > p.baseRadius * 1.3) {
          // Hovered / near mouse: white with slight warm tint
          ctx.fillStyle = `rgba(255, 255, 255, ${currentOpacity})`;
        } else {
          ctx.fillStyle = `rgba(200, 200, 200, ${currentOpacity})`;
        }
        ctx.fill();
      });

      // Subtle sweeping light beam
      const beamX = ((time * 0.02) % (w + 400)) - 200;
      const beamGrad = ctx.createLinearGradient(beamX - 100, 0, beamX + 100, h);
      beamGrad.addColorStop(0, "rgba(220, 38, 38, 0)");
      beamGrad.addColorStop(0.5, "rgba(220, 38, 38, 0.02)");
      beamGrad.addColorStop(1, "rgba(220, 38, 38, 0)");
      ctx.fillStyle = beamGrad;
      ctx.fillRect(beamX - 100, 0, 200, h);

      animId = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 2 }}
    />
  );
}